var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack, TextField } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { lazy, useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { createWebhookAction, updateWebhookAction } from '../../../api/action/webhook.action';
import { QUERY_KEY } from '../../../api/types/query.type';
import { useSettings } from '../../../context';
import { createOrUpdateWebhookFormSchema } from '../../../schemas/webhook.schemas';
import { ShopAction, WebhookSite, WebhookType } from '../../../types/webhooks.type';
import { FORM_INPUTS } from './form.costants';
import { theme } from './theme';
var ButtonComponent = lazy(function () { return import('../../UIcomponents/ButtonComponent/ButtonComponent'); });
var AlertComponent = lazy(function () { return import('../../UIcomponents/AlertComponent/AlertComponent'); });
var Loader = lazy(function () { return import('../../UIcomponents/Loader'); });
var WebhookForm = function (_a) {
    var shopId = _a.shopId, webhookValue = _a.webhookValue, _b = _a.isUpdateMode, isUpdateMode = _b === void 0 ? false : _b, onCancel = _a.onCancel, webhookId = _a.webhookId, onWebhookCreated = _a.onWebhookCreated;
    var _c = useState(false), needToShowErrorAlert = _c[0], setNeedToShowErrorAlert = _c[1];
    var t = useTranslation('settings').t;
    var userPortal = useSettings().userPortal;
    var _d = useForm({
        resolver: zodResolver(createOrUpdateWebhookFormSchema()),
        defaultValues: {
            webhook_value: webhookValue || '',
        },
        mode: 'all',
    }), control = _d.control, handleSubmit = _d.handleSubmit;
    var queryClient = useQueryClient();
    var _e = useMutation({
        mutationFn: createWebhookAction,
        onSuccess: function () {
            onWebhookCreated === null || onWebhookCreated === void 0 ? void 0 : onWebhookCreated();
        },
        onError: function () {
            setNeedToShowErrorAlert(true);
            setTimeout(function () {
                setNeedToShowErrorAlert(false);
            }, 2000);
        },
    }), createUspacyWebhook = _e.mutate, isCreatingWebhook = _e.isPending, isCreatedWebhook = _e.isSuccess;
    var _f = useMutation({
        mutationFn: createWebhookAction,
        onSuccess: function () {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEY.GET_WEBHOOKS, userPortal],
            });
        },
        onError: function () {
            setNeedToShowErrorAlert(true);
            setTimeout(function () {
                setNeedToShowErrorAlert(false);
            }, 2000);
        },
    }), createInternalWebhook = _f.mutate, isInternalCreatingWebhook = _f.isPending;
    var _g = useMutation({
        mutationFn: updateWebhookAction,
        onSuccess: function () {
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEY.GET_WEBHOOKS],
            });
            onWebhookCreated === null || onWebhookCreated === void 0 ? void 0 : onWebhookCreated();
        },
        onError: function () {
            setNeedToShowErrorAlert(true);
            setTimeout(function () {
                setNeedToShowErrorAlert(false);
            }, 2000);
        },
    }), updateUspacyWebhook = _g.mutate, isUpdatingWebhook = _g.isPending;
    useEffect(function () {
        if (!isCreatedWebhook || !shopId)
            return;
        createInternalWebhook({
            shop_id: shopId,
            shop_action: ShopAction.ORDER_CREATION,
            webhook_type: WebhookType.INCOMING,
            webhook_site: WebhookSite.INTERNAL,
        });
    }, [isCreatedWebhook, createInternalWebhook, shopId]);
    var onSubmit = useCallback(function (data) {
        if (isUpdateMode && webhookId) {
            updateUspacyWebhook({
                webhook_id: webhookId,
                webhook_value: data.webhook_value,
            });
        }
        else {
            createUspacyWebhook(__assign(__assign({}, data), { shop_id: shopId, shop_action: ShopAction.ORDER_CREATION, webhook_type: WebhookType.INCOMING, webhook_site: WebhookSite.USPACY }));
        }
    }, [createUspacyWebhook, shopId, isUpdateMode, updateUspacyWebhook, webhookId]);
    var isLoading = isCreatingWebhook || isInternalCreatingWebhook || isUpdatingWebhook;
    if (isLoading) {
        return (_jsx(Box, { sx: theme.container, children: _jsx(Loader, { type: "form", fieldsCount: 1 }) }));
    }
    if (needToShowErrorAlert) {
        return (_jsx(Box, { sx: theme.container, children: _jsx(Box, { sx: {
                    minHeight: '60px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }, children: _jsx(AlertComponent, { type: "error", message: t('webhook.error.createWebhook') }) }) }));
    }
    return (_jsx(Box, { component: "form", onSubmit: handleSubmit(onSubmit), sx: theme.container, children: _jsxs(Box, { sx: theme.inputsWrapper, children: [FORM_INPUTS.map(function (_a) {
                    var webhookName = _a.name, label = _a.label, placeholder = _a.placeholder;
                    return (_jsx(Controller, { name: webhookName, control: control, render: function (_a) {
                            var field = _a.field, error = _a.fieldState.error;
                            return (_jsx(TextField, __assign({ fullWidth: true }, field, { label: t(label), placeholder: t(placeholder), variant: "outlined", error: !!error, helperText: error ? t(error.message) : null })));
                        } }, label));
                }), _jsx(Stack, { direction: "row", spacing: 2, justifyContent: "center", children: !isUpdateMode ? (_jsx(ButtonComponent, { title: t('webhook.submit'), type: "submit", isDisabled: isCreatingWebhook })) : (_jsxs(_Fragment, { children: [_jsx(ButtonComponent, { title: t('common.cancel'), stylesType: "cancel", isDisabled: isUpdatingWebhook, handleClick: onCancel }), _jsx(ButtonComponent, { title: t('common.update'), type: "submit", isDisabled: isUpdatingWebhook })] })) })] }) }));
};
export default WebhookForm;
